import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  Search, 
  Bell, 
  Mail, 
  Bookmark, 
  User, 
  Settings, 
  MoreHorizontal, 
  Plus, 
  LogOut,
  Sun,
  Moon,
  Tv
} from 'lucide-react';
import { BrandLogo } from '@/components/ui/BrandLogo';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLoggedInAccounts } from '@/hooks/useLoggedInAccounts';
import { LoginArea } from '@/components/auth/LoginArea';
import { AccountSwitcher } from '@/components/auth/AccountSwitcher';
import { useTheme } from '@/hooks/useTheme';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface LeftSidebarProps {
  onOpenCompose: () => void;
}

export function LeftSidebar({ onOpenCompose }: LeftSidebarProps) {
  const { user } = useCurrentUser();
  const accounts = useLoggedInAccounts();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();

  const userNpub = user ? nip19.npubEncode(user.pubkey) : '';
  const author = useAuthor(user?.pubkey ?? '');
  const metadata = author.data?.metadata;

  const menuItems = [
    { label: 'Home', path: '/', icon: Home, show: true },
    { label: 'Explore', path: '/explore', icon: Search, show: true },
    { label: 'Chat', path: '/chat', icon: Mail, show: true },
    { label: 'Notifications', path: '/notifications', icon: Bell, show: true },
    { label: 'Bookmarks', path: '/bookmarks', icon: Bookmark, show: !!user },
    { label: 'Profile', path: userNpub ? `/${userNpub}` : '#', icon: User, show: !!user },
    { label: 'Settings', path: '/settings', icon: Settings, show: true },
  ];

  return (
    <aside className="hidden md:flex flex-col items-end xl:items-start h-screen sticky top-0 py-4 px-2 xl:px-4 border-r border-border w-16 xl:w-64 select-none justify-between shrink-0">
      <div className="flex flex-col items-center xl:items-start gap-2 w-full">
        {/* Logo */}
        <NavLink to="/" className="p-3 hover:bg-muted rounded-full transition-colors flex items-center justify-center self-center xl:self-start">
          <BrandLogo className="size-7" />
        </NavLink>

        {/* Menu Items */}
        <nav className="flex flex-col gap-1 w-full mt-2">
          {menuItems.map((item) => {
            if (!item.show) return null;
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            return (
              <NavLink
                key={item.label}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center gap-4 p-3 rounded-full hover:bg-secondary/40 transition-colors w-fit
                  ${isActive ? 'font-bold text-foreground' : 'text-foreground/80'}
                `}
                onClick={(e) => {
                  if (item.path === '#') e.preventDefault();
                }}
              >
                <Icon className={`size-6 ${isActive ? 'stroke-[2.5px]' : 'stroke-2'}`} />
                <span className="hidden xl:inline text-xl">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Post Button / Sign in area */}
        {user ? (
          <Button 
            className="w-12 h-12 xl:w-full xl:h-12 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-lg flex items-center justify-center mt-4 p-0 shadow-sm cursor-pointer"
            onClick={onOpenCompose}
          >
            <span className="hidden xl:inline">Post</span>
            <Plus className="xl:hidden size-6" />
          </Button>
        ) : (
          <div className="mt-4 xl:w-full pr-1.5 xl:pr-0">
            {/* LoginArea handles the primary blue join action button for logged out users */}
            <LoginArea className="w-11 h-11 xl:h-auto xl:w-full" />
          </div>
        )}
      </div>

      {/* Footer Profile Menu & Theme Fast Switcher */}
      <div className="flex flex-col items-center xl:items-stretch gap-3 w-full">
        {/* Quick Theme Switcher */}
        <div className="hidden xl:flex items-center justify-between p-2 mx-2 bg-secondary/30 rounded-full border border-border/80">
          <button 
            onClick={() => setTheme('light')} 
            className={`p-1.5 rounded-full transition-all ${theme === 'light' ? 'bg-background text-sky-500 shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}
            title="Light Mode"
          >
            <Sun className="size-4" />
          </button>
          <button 
            onClick={() => setTheme('dim')} 
            className={`p-1.5 rounded-full transition-all ${theme === 'dim' ? 'bg-background text-sky-500 shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}
            title="Dim Mode"
          >
            <Tv className="size-4" />
          </button>
          <button 
            onClick={() => setTheme('dark')} 
            className={`p-1.5 rounded-full transition-all ${theme === 'dark' ? 'bg-background text-sky-500 shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}
            title="Lights Out"
          >
            <Moon className="size-4" />
          </button>
        </div>

        {/* User profile switcher footer */}
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center justify-between p-2 rounded-full hover:bg-secondary/40 transition-colors w-full cursor-pointer text-left">
                <div className="flex items-center gap-3">
                  <img 
                    src={metadata?.picture || `https://robohash.org/${user.pubkey}.png?set=set4`}
                    alt={metadata?.name || 'User'} 
                    className="size-10 rounded-full object-cover border border-border"
                  />
                  <div className="hidden xl:flex flex-col leading-tight max-w-[120px]">
                    <span className="font-bold truncate text-[15px]">{metadata?.display_name || metadata?.name || 'Anonymous'}</span>
                    <span className="text-xs text-muted-foreground truncate">@{userNpub.slice(0, 8)}...{userNpub.slice(-4)}</span>
                  </div>
                </div>
                <MoreHorizontal className="hidden xl:block text-muted-foreground size-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 rounded-2xl p-2 shadow-xl border border-border">
              <AccountSwitcher onAddAccountClick={() => {}} />
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </aside>
  );
}
